import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  TreePine, Camera, Upload, CheckCircle2, AlertCircle, Loader2,
  Navigation, X, MapPin, Clock, Image, Shield
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

function PhotoGrid({ photos, onRemove, label, onAdd, maxPhotos = 5 }) {
  return (
    <div className="mb-6">
      <label className="text-sm font-semibold text-slate-200 mb-3 block">{label}</label>
      <div className="grid grid-cols-3 gap-3">
        {photos.map((file, idx) => (
          <div key={idx} className="relative aspect-square rounded-xl overflow-hidden bg-slate-800 border border-slate-700">
            <img src={URL.createObjectURL(file)} alt={`${label} ${idx + 1}`} className="w-full h-full object-cover" />
            <button
              onClick={() => onRemove(idx)}
              className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500/90 text-white rounded-full flex items-center justify-center backdrop-blur-sm"
              data-testid={`remove-photo-${label.toLowerCase().replace(/\s/g, '-')}-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <label className="aspect-square border-2 border-dashed border-slate-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-500/5 transition-all">
            <Camera className="w-7 h-7 text-slate-400 mb-1" />
            <span className="text-xs text-slate-400">Tap to add</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onAdd(Array.from(e.target.files))}
              data-testid={`photo-input-${label.toLowerCase().replace(/\s/g, '-')}`}
            />
          </label>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2">{photos.length}/{maxPhotos} photos</p>
    </div>
  );
}

const ProofUploadPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [missionInfo, setMissionInfo] = useState(null);
  const [error, setError] = useState(null);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [verificationHash, setVerificationHash] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API}/marketplace/proof-upload/${token}`);
        if (res.ok) {
          const data = await res.json();
          setMissionInfo(data);
        } else {
          const errData = await res.json().catch(() => ({}));
          if (res.status === 410) {
            setError(errData.detail || 'This upload link has expired or already been used.');
          } else {
            setError(errData.detail || 'Invalid upload link.');
          }
        }
      } catch {
        setError('Could not connect to server.');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported on this device');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGettingLocation(false);
        toast.success('Location captured');
      },
      () => {
        toast.error('Could not get location. Enable location services.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleSubmit = async () => {
    if (beforePhotos.length === 0) return toast.error('Add at least one "before" photo');
    if (afterPhotos.length === 0) return toast.error('Add at least one "after" photo');

    setSubmitting(true);
    setUploadProgress(10);

    const formData = new FormData();
    beforePhotos.forEach((p) => formData.append('before_photos', p));
    afterPhotos.forEach((p) => formData.append('after_photos', p));
    formData.append('notes', notes);
    if (gps.lat && gps.lng) {
      formData.append('gps_lat', gps.lat);
      formData.append('gps_lng', gps.lng);
    }

    setUploadProgress(30);

    try {
      const res = await fetch(`${API}/marketplace/proof-upload/${token}/submit`, {
        method: 'POST',
        body: formData
      });

      setUploadProgress(90);

      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setVerificationHash(data.verification_hash);
        setUploadProgress(100);
        toast.success('Proof submitted!');
      } else {
        toast.error(data.detail || 'Submission failed');
        setUploadProgress(0);
      }
    } catch {
      toast.error('Network error. Try again.');
      setUploadProgress(0);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Link Unavailable</h1>
        <p className="text-slate-400 mb-6">{error}</p>
        <Link to="/worker/login">
          <Button variant="outline" className="border-slate-700 text-slate-300" data-testid="go-to-login">
            Go to Worker Portal
          </Button>
        </Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mb-6">
          <CheckCircle2 className="w-20 h-20 text-emerald-400" />
        </motion.div>
        <h1 className="text-2xl font-bold text-white mb-2">Proof Submitted</h1>
        <p className="text-slate-400 mb-4">
          Your proof has been received and is awaiting admin review. You will be notified when it is verified.
        </p>
        {verificationHash && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 w-full max-w-sm mb-6">
            <div className="flex items-center gap-2 text-emerald-400 mb-2">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-medium">Verification Hash</span>
            </div>
            <p className="text-xs text-slate-500 font-mono break-all" data-testid="verification-hash">
              {verificationHash}
            </p>
          </div>
        )}
        <Link to="/">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="back-home">
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <TreePine className="w-6 h-6 text-emerald-500" />
          <span className="font-bold text-white text-sm">Epuyen Renace</span>
          <span className="text-slate-600 text-sm">|</span>
          <span className="text-xs text-slate-400">Proof Upload</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Mission Info */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-4">
            <h2 className="text-lg font-bold text-white mb-3" data-testid="mission-name">
              {missionInfo.zone_name}
            </h2>
            <div className="flex flex-wrap gap-4 text-sm text-slate-400">
              {missionInfo.hectares && (
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-500" />{missionInfo.hectares} ha</span>
              )}
              {missionInfo.bounty_amount && (
                <span className="flex items-center gap-1.5"><TreePine className="w-4 h-4 text-emerald-500" />${missionInfo.bounty_amount.toLocaleString()}</span>
              )}
            </div>
            {missionInfo.instructions && (
              <div className="mt-3 bg-slate-800/50 rounded-lg p-3">
                <p className="text-xs text-slate-400 font-medium mb-1">Instructions:</p>
                <p className="text-sm text-slate-300">{missionInfo.instructions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Form */}
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Image className="w-5 h-5 text-emerald-500" /> Upload Proof Photos
          </h3>
          <p className="text-xs text-slate-500 mb-4">Take or upload before and after photos of the work area.</p>

          <PhotoGrid
            photos={beforePhotos}
            label="Before Photos"
            maxPhotos={5}
            onAdd={(files) => setBeforePhotos(prev => [...prev, ...files.slice(0, 5 - prev.length)])}
            onRemove={(idx) => setBeforePhotos(prev => prev.filter((_, i) => i !== idx))}
          />

          <PhotoGrid
            photos={afterPhotos}
            label="After Photos"
            maxPhotos={5}
            onAdd={(files) => setAfterPhotos(prev => [...prev, ...files.slice(0, 5 - prev.length)])}
            onRemove={(idx) => setAfterPhotos(prev => prev.filter((_, i) => i !== idx))}
          />

          {/* GPS */}
          <div className="mb-6">
            <label className="text-sm font-semibold text-slate-200 mb-3 block">GPS Location</label>
            <Button
              variant="outline"
              onClick={handleGetLocation}
              disabled={gettingLocation}
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="capture-gps"
            >
              {gettingLocation ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Getting location...</>
              ) : gps.lat ? (
                <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" /> {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</>
              ) : (
                <><Navigation className="w-4 h-4 mr-2" /> Capture Current Location</>
              )}
            </Button>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="text-sm font-semibold text-slate-200 mb-3 block">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the work completed..."
              rows={3}
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
              data-testid="proof-notes"
            />
          </div>

          {/* Upload Progress */}
          <AnimatePresence>
            {submitting && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mb-4">
                <Progress value={uploadProgress} className="h-2 mb-2" />
                <p className="text-xs text-slate-400 text-center">Uploading proof... {uploadProgress}%</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || beforePhotos.length === 0 || afterPhotos.length === 0}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold"
            data-testid="submit-proof-button"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
            ) : (
              <><Upload className="w-5 h-5 mr-2" /> Submit Proof of Work</>
            )}
          </Button>

          <p className="text-xs text-slate-600 text-center mt-3">
            <Clock className="w-3 h-3 inline mr-1" />
            This link expires on {new Date(missionInfo.expires_at).toLocaleDateString()}
          </p>
        </div>
      </main>
    </div>
  );
};

export default ProofUploadPage;
